import { useToast } from "@/components/ui/use-toast";
import { Escalation } from "@/lib/api/escalations";
import { useEvents } from "@/lib/hooks/use-events";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

interface EscalationEventPayload extends Escalation {}

export function useEscalationNotification() {
  const { toast } = useToast();
  const { addEventListener } = useEvents();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleEscalationCreated = (payload: EscalationEventPayload) => {
      toast({
        title: `New Escalation for ${payload.mentee?.name}`,
        description: `Escalation reported for teen "${payload.mentee?.name}" by ${payload.started_by?.name}${
          payload.reason ? ` for ${payload.reason}` : ""
        }`,
        variant: "destructive",
      });

      queryClient.invalidateQueries({
        predicate(query) {
          return (
            query.queryKey[0] === "escalations" ||
            query.queryKey[0] === "escalation" ||
            query.queryKey[0] === "mentorships"
          );
        },
      });
    };

    const handleEscalationClosed = () => {
      queryClient.invalidateQueries({
        predicate(query) {
          return (
            query.queryKey[0] === "escalations" ||
            query.queryKey[0] === "escalation" ||
            query.queryKey[0] === "mentorships"
          );
        },
      });
    };

    // Add event listeners for escalation events
    const cleanupCreate = addEventListener<EscalationEventPayload>(
      "ESCALATION_CREATED",
      handleEscalationCreated,
    );

    const cleanupClose = addEventListener<EscalationEventPayload>(
      "ESCALATION_CLOSED",
      handleEscalationClosed,
    );

    return () => {
      cleanupCreate();
      cleanupClose();
    };
  }, [addEventListener, toast, queryClient]);
}
